import { Injectable, OnDestroy, inject } from '@angular/core';
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { Subject } from 'rxjs';
import { environment } from '../../../enviroment';
import { AuthService } from '../../core/services/auth.service';
import { ChatConversation, ChatMessage, UserPresence } from './chat.models';

export interface ChatTypingEvent {
  roomId: string;
  userId: string;
  displayName: string;
  isTyping: boolean;
}

export interface ChatDeletedEvent {
  roomId: string;
  messageId: string;
  forEveryone: boolean;
}

export interface ChatReadEvent {
  roomId: string;
  userId: string;
  messageId?: string;
  readAt: string;
}

@Injectable({ providedIn: 'root' })
export class ChatRealtimeService implements OnDestroy {
  private readonly auth = inject(AuthService);
  private connection: HubConnection | null = null;

  readonly messageCreated$ = new Subject<ChatMessage>();
  readonly messageUpdated$ = new Subject<ChatMessage>();
  readonly messageDeleted$ = new Subject<ChatDeletedEvent>();
  readonly typing$ = new Subject<ChatTypingEvent>();
  readonly presenceChanged$ = new Subject<UserPresence>();
  readonly messageRead$ = new Subject<ChatReadEvent>();
  readonly conversationUpdated$ = new Subject<ChatConversation>();

  private hubUrl(): string {
    return (environment.apiOrigin || 'http://hr-api.trustedpos.org').replace(/\/$/, '') + '/hubs/chat';
  }

  async ensureConnected(): Promise<HubConnection> {
    if (this.connection?.state === HubConnectionState.Connected) {
      return this.connection;
    }

    if (!this.connection) {
      this.connection = new HubConnectionBuilder()
        .withUrl(this.hubUrl(), {
          accessTokenFactory: () => this.auth.getAccessToken() ?? ''
        })
        .withAutomaticReconnect()
        .configureLogging(LogLevel.Warning)
        .build();

      this.connection.on('messageCreated', (m: ChatMessage) => this.messageCreated$.next(m));
      this.connection.on('messageUpdated', (m: ChatMessage) => this.messageUpdated$.next(m));
      this.connection.on('messageDeleted', (e: ChatDeletedEvent) => this.messageDeleted$.next(e));
      this.connection.on('userTyping', (e: ChatTypingEvent) => this.typing$.next(e));
      this.connection.on('presenceChanged', (p: UserPresence) => this.presenceChanged$.next(p));
      this.connection.on('messageRead', (e: ChatReadEvent) => this.messageRead$.next(e));
      this.connection.on('conversationUpdated', (c: ChatConversation) => this.conversationUpdated$.next(c));
    }

    if (this.connection.state === HubConnectionState.Disconnected) {
      await this.connection.start();
    }

    return this.connection;
  }

  async joinRoom(roomId: string): Promise<void> {
    const conn = await this.ensureConnected();
    await conn.invoke('JoinRoom', roomId);
  }

  async leaveRoom(roomId: string): Promise<void> {
    if (this.connection?.state !== HubConnectionState.Connected) return;
    await this.connection.invoke('LeaveRoom', roomId);
  }

  async notifyTyping(roomId: string, isTyping: boolean): Promise<void> {
    if (this.connection?.state !== HubConnectionState.Connected) return;
    await this.connection.invoke('UserTyping', roomId, isTyping);
  }

  async markRead(roomId: string, messageId?: string | null): Promise<void> {
    if (this.connection?.state !== HubConnectionState.Connected) return;
    await this.connection.invoke('MessageRead', roomId, messageId ?? null);
  }

  ngOnDestroy(): void {
    void this.connection?.stop();
    this.messageCreated$.complete();
    this.messageUpdated$.complete();
    this.messageDeleted$.complete();
    this.typing$.complete();
    this.presenceChanged$.complete();
    this.messageRead$.complete();
    this.conversationUpdated$.complete();
  }
}
